import React, { useState } from 'react';
import { PaperPlaneRight, Robot, UserCircle, Sparkle, LockOpen, Handshake } from '@phosphor-icons/react';
import LivingBubble from '@/components/LivingBubble';
import { bubbleImageSrc } from '@/lib/bubbleImage';

const REACTIONS = [['love', 'Love it'], ['wow', 'Wow'], ['laugh', 'Ha!'], ['curious', 'Tell me more']];

const Section = ({ label, children, testid }) => (
  <div className="mt-4" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-[0.22em] text-white/60 font-bold">{label}</p>
    <div className="mt-1">{children}</div>
  </div>
);

const Who = ({ enc }) => {
  const ai = enc.kind === 'ai_character';
  return (
    <div className="flex items-center gap-3">
      {ai ? <LivingBubble bubble={null} tint={enc.tint} mood={enc.mood} className="w-14" testid="encounter-avatar" />
          : <img src={bubbleImageSrc(enc)} alt={enc.name} className="w-14 h-14 rounded-full object-cover" />}
      <div>
        <p className="text-white font-bold">{enc.name}</p>
        <span data-testid="encounter-label" className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-bold ${
          ai ? 'bg-bx-cyan/15 text-bx-cyan' : 'bg-bx-volt/15 text-bx-volt'}`}>
          {ai ? <Robot size={12} weight="bold" /> : <UserCircle size={12} weight="bold" />} {enc.label}
        </span>
      </div>
    </div>
  );
};

const AiActions = ({ card, onReply, onReact, busy }) => {
  const [text, setText] = useState('');
  const send = (e) => { e.preventDefault(); if (text.trim()) { onReply(text.trim()); setText(''); } };
  return (
    <>
      {card.encounter.line && <p className="mt-3 text-sm text-white/80 italic">{card.encounter.name}: “{card.encounter.line}”</p>}
      {(card.replies || []).map((r, i) => (
        <p key={i} className={`mt-2 text-sm ${r.by === 'creator' ? 'text-white text-right' : 'text-bx-cyan'}`} data-testid={`adventure-reply-${i}`}>
          {r.by === 'creator' ? r.text : `${card.encounter.name}: ${r.text}`}
        </p>
      ))}
      <form onSubmit={send} className="mt-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} data-testid="adventure-reply-input"
               placeholder={`Say something to ${card.encounter.name}…`}
               className="flex-1 bg-white/5 border border-white/15 rounded-full px-4 py-2 text-sm text-white placeholder:text-white/45 focus:outline-none focus:border-bx-cyan" />
        <button disabled={busy || !text.trim()} data-testid="adventure-reply-send" className="px-4 rounded-full bg-white text-black disabled:opacity-40"><PaperPlaneRight weight="bold" /></button>
      </form>
      <div className="mt-3 flex flex-wrap gap-2">
        {REACTIONS.map(([k, l]) => (
          <button key={k} onClick={() => onReact(k)} data-testid={`adventure-react-${k}`}
                  className={`text-xs px-3 py-1 rounded-full border transition-colors ${card.reaction === k ? 'bg-bx-pink text-black border-bx-pink' : 'border-white/20 text-white/80 hover:bg-white/10'}`}>{l}</button>
        ))}
      </div>
    </>
  );
};

const RealActions = ({ card, onIntroduce, busy }) => {
  const st = card.intro_status;
  if (st === 'asked') return <p className="mt-3 text-sm text-white/75" data-testid="intro-status-asked">I asked. They'll decide too — nothing is shared unless you both say yes.</p>;
  if (st === 'accepted') return <p className="mt-3 text-sm text-bx-volt" data-testid="intro-status-accepted">You're connected! Find them under “People you've met”.</p>;
  if (st === 'declined') return <p className="mt-3 text-sm text-white/70" data-testid="intro-status-declined">Not this time. That's okay.</p>;
  return (
    <button onClick={onIntroduce} disabled={busy} data-testid="adventure-introduce-btn" className="mt-3 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-bx-volt text-black font-bold text-sm">
      <Handshake size={16} weight="bold" /> Yes, introduce us
    </button>
  );
};

export const AdventureCard = ({ bubble, card, onReply, onReact, onIntroduce, onContinue, busy }) => {
  const ai = card.encounter?.kind === 'ai_character';
  return (
    <div className="home-card home-pop" data-testid="adventure-card">
      <p className="text-xs uppercase tracking-[0.25em] text-bx-cyan font-bold">{bubble.name} came home</p>
      <p className="mt-3 text-lg leading-relaxed text-white" data-testid="adventure-bubble-line">“{card.bubble_line}”</p>
      <Section label="Where it went" testid="adventure-where">
        <span className="home-chip text-sm text-white/90" style={{ borderColor: card.place_tint }}>{card.place}</span>
      </Section>
      <Section label="Who it met" testid="adventure-who"><Who enc={card.encounter} /></Section>
      <Section label={`What happened · ${card.happened.label}`} testid="adventure-happened">
        <p className="text-white/85 text-sm leading-relaxed">{card.happened.text}</p>
      </Section>
      <Section label="What it discovered" testid="adventure-discovery">
        <p className="text-white font-bold inline-flex items-center gap-1.5"><Sparkle weight="fill" className="text-bx-gold" /> {card.discovery.title}</p>
        <p className="text-white/80 text-sm">{card.discovery.text}</p>
        {card.discovery.unlocked_place && (
          <p className="mt-2 text-sm text-bx-gold inline-flex items-center gap-1.5" data-testid="adventure-unlocked">
            <LockOpen weight="bold" /> New place unlocked: {card.discovery.unlocked_place.name}
          </p>
        )}
      </Section>
      {card.real_events?.length > 0 && (
        <Section label="Also really happened while it was out" testid="adventure-real-events">
          {card.real_events.map((e, i) => <p key={i} className="text-sm text-white/85">{e.text}</p>)}
        </Section>
      )}
      <Section label="What happens next" testid="adventure-next">
        {ai ? <AiActions card={card} onReply={onReply} onReact={onReact} busy={busy} /> : <RealActions card={card} onIntroduce={onIntroduce} busy={busy} />}
      </Section>
      <button onClick={onContinue} disabled={busy} data-testid="adventure-continue-btn"
              className="mt-6 w-full btn-volt px-6 py-3 rounded-full">Keep exploring</button>
    </div>
  );
};

export default AdventureCard;
